import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { message } from "antd";
import { API_BASE } from "../config";

export const FavoritesContext = createContext();

// NOT: Backend'de de aynı limit kontrolü yapılmalı (UserFavorite controller'ında)
const MAX_FAVORITES = 10;
const FAVORITES_KEY_PREFIX = "favorites";

const parseStoredUser = () => {
    try {
        const stored = localStorage.getItem("user");
        return stored ? JSON.parse(stored) : null;
    } catch {
        return null;
    }
};

const getStorageKey = () => {
    const dbName = localStorage.getItem("dbName") || "default";
    const user = parseStoredUser();
    const identifier =
        user?.email ||
        user?.adminEmail ||
        user?.fullName ||
        user?.adminFullName ||
        "guest";
    return `${FAVORITES_KEY_PREFIX}:${dbName}:${identifier}`.toLowerCase();
};

const getAuthHeaders = () => {
    const headers = {
        "Content-Type": "application/json",
    };
    const dbName = localStorage.getItem("dbName");
    if (dbName) {
        headers.DbName = dbName;
    }
    const token = parseStoredUser()?.token;
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    return headers;
};

export const FavoritesProvider = ({ children }) => {
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(false);
    const storageKey = useMemo(() => getStorageKey(), []);

    const loadFromStorage = () => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                setFavorites(JSON.parse(stored));
            } else {
                setFavorites([]);
            }
        } catch {
            // ignore corrupted data
            setFavorites([]);
        }
    };

    // API'den favorileri yükle
    const loadFavorites = async () => {
        setLoading(true);
        const headers = getAuthHeaders();
        if (!headers.Authorization) {
            loadFromStorage();
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${API_BASE}/UserFavorite`, {
                headers,
            });

            if (response.ok) {
                const data = await response.json();
                setFavorites(data);
            } else {
                const errorData = await response.json().catch(() => ({}));
                message.error(errorData?.message || "Favoriler yüklenemedi.");
                loadFromStorage();
            }
        } catch (error) {
            console.error("Favoriler yüklenirken hata:", error);
            loadFromStorage();
        } finally {
            setLoading(false);
        }
    };

    // Component mount olduğunda favorileri yükle
    useEffect(() => {
        const user = localStorage.getItem("user");
        if (user) {
            loadFavorites();
        }
    }, []);

    // Favorileri localStorage'a kaydet (fallback için)
    useEffect(() => {
        localStorage.setItem(storageKey, JSON.stringify(favorites));
    }, [favorites, storageKey]);

    const handleOfflineAdd = (favorite) => {
        setFavorites(prev => [...prev, { ...favorite, id: favorite.id || Date.now() }]);
        message.success("Favorilere eklendi (yerel).");
        return true;
    };

    const handleOfflineRemove = (menuKey) => {
        setFavorites(prev => prev.filter(f => f.menuKey !== menuKey));
        message.success("Favorilerden kaldırıldı (yerel).");
        return true;
    };

    // Favori ekle
    const addFavorite = async (menuItem) => {
        // Limit kontrolü
        if (favorites.length >= MAX_FAVORITES) {
            message.warning(`En fazla ${MAX_FAVORITES} favori ekleyebilirsiniz.`);
            return false;
        }

        // Zaten favori mi kontrol et
        if (favorites.some(f => f.menuKey === menuItem.key)) {
            message.info("Bu menü zaten favorilerinizde.");
            return false;
        }

        const newFavorite = {
            menuKey: menuItem.key,
            menuLabel: menuItem.label,
            menuIcon: menuItem.icon,
            displayOrder: favorites.length,
        };

        const headers = getAuthHeaders();
        if (!headers.Authorization) {
            return handleOfflineAdd(newFavorite);
        }

        try {
            const response = await fetch(`${API_BASE}/UserFavorite`, {
                method: "POST",
                headers,
                body: JSON.stringify(newFavorite),
            });

            if (response.ok) {
                const savedFavorite = await response.json();
                setFavorites(prev => [...prev, savedFavorite]);
                message.success("Favorilere eklendi.");
                return true;
            }

            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData?.message || "Favori eklenemedi.");
        } catch (error) {
            console.error("Favori eklenirken hata:", error);
            if (error instanceof TypeError) {
                return handleOfflineAdd(newFavorite);
            }
            message.error(error.message || "Favori eklenemedi.");
            return false;
        }
    };

    // Favori sil
    const removeFavorite = async (menuKey) => {
        const headers = getAuthHeaders();
        if (!headers.Authorization) {
            return handleOfflineRemove(menuKey);
        }

        try {
            const response = await fetch(`${API_BASE}/UserFavorite/${menuKey}`, {
                method: "DELETE",
                headers,
            });

            if (response.ok) {
                setFavorites(prev => prev.filter(f => f.menuKey !== menuKey));
                message.success("Favorilerden kaldırıldı.");
                return true;
            }

            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData?.message || "Favori silinemedi.");
        } catch (error) {
            console.error("Favori silinirken hata:", error);
            if (error instanceof TypeError) {
                return handleOfflineRemove(menuKey);
            }
            message.error(error.message || "Favori silinemedi.");
            return false;
        }
    };

    // Menü favorilerde mi kontrol et
    const isFavorite = (menuKey) => {
        return favorites.some(f => f.menuKey === menuKey);
    };

    // Favorileri temizle (logout için)
    const clearFavorites = () => {
        setFavorites([]);
        localStorage.removeItem(storageKey);
    };

    return (
        <FavoritesContext.Provider
            value={{
                favorites,
                loading,
                addFavorite,
                removeFavorite,
                isFavorite,
                clearFavorites,
                loadFavorites,
                maxFavorites: MAX_FAVORITES,
            }}
        >
            {children}
        </FavoritesContext.Provider>
    );
};

export const useFavorites = () => useContext(FavoritesContext);
